import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { StandardMaterialDialog } from "@/pages/admin/einstellungen/components/StandardMaterialDialog";
import {
  createSOrdnerStandardMaterial,
  deleteSOrdnerStandardMaterial,
  listSOrdnerStandardMaterials,
  updateSOrdnerStandardMaterial,
  type StandardMaterialOut,
} from "@/pages/admin/einstellungen/lib/settingsApi";

export function SOrdnerStandardMaterialSection({
  sordnerId,
  nummer,
  beschreibung,
}: {
  sordnerId: number;
  nummer: string;
  beschreibung?: string | null;
}) {
  const [items, setItems] = useState<StandardMaterialOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listSOrdnerStandardMaterials(sordnerId));
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : "Standardmaterialien konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [sordnerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const api = useMemo(
    () => ({
      list: () => listSOrdnerStandardMaterials(sordnerId),
      create: (body: Parameters<typeof createSOrdnerStandardMaterial>[1]) =>
        createSOrdnerStandardMaterial(sordnerId, body),
      update: (id: number, body: Parameters<typeof updateSOrdnerStandardMaterial>[2]) =>
        updateSOrdnerStandardMaterial(sordnerId, id, body),
      remove: (id: number) => deleteSOrdnerStandardMaterial(sordnerId, id),
    }),
    [sordnerId],
  );

  const title = `Standardmaterialien – ${nummer}${beschreibung ? ` ${beschreibung}` : ""}`;

  return (
    <>
      <OverviewCard title="Standardmaterialien">
        <p className="mb-3 text-sm text-label-secondary">
          Diese Materialien werden bei neuen Stundennachweisen in Projekten dieses S-Ordners
          übernommen. Ohne Einträge gelten die Standardmaterialien des Kunden.
        </p>

        {error ? (
          <Alert variant="error" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-label-secondary">
            <Spinner className="h-4 w-4" />
            Materialien werden geladen …
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-label-secondary">Noch keine Standardmaterialien hinterlegt.</p>
        ) : (
          <ul className="divide-y divide-separator">
            {items.map((item) => (
              <li key={item.id} className="py-2">
                <div className="text-sm font-medium text-label">{item.text}</div>
                <div className="text-xs text-label-secondary">
                  {item.menge} {item.einheit || ""}
                  {item.hero_nr ? ` · Hero ${item.hero_nr}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}

        <Button type="button" variant="secondary" size="sm" className="mt-3" onClick={() => setEditorOpen(true)}>
          Standardmaterialien bearbeiten
        </Button>
      </OverviewCard>

      <StandardMaterialDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          void reload();
        }}
        title={title}
        api={api}
      />
    </>
  );
}
